import { useState, useEffect } from 'react';
import { VStack, Button, Text, NumberInput, NumberInputField } from '@chakra-ui/react';
import type { ValidationRule } from '@/lib/sdk/workflows/types';

interface NumberInputTaskProps {
  description: string;
  validation: ValidationRule;
  onSubmit: (value: string) => void;
  isLoading?: boolean;
  // Controlled mode props
  controlled?: boolean;
  onValueChange?: (value: string) => void;
  initialValue?: string;
}

export function NumberInputTask({
  description,
  validation,
  onSubmit,
  isLoading = false,
  controlled = false,
  onValueChange,
  initialValue = '',
}: NumberInputTaskProps) {
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState('');

  // Update value when initialValue changes (for controlled mode)
  useEffect(() => {
    if (initialValue !== undefined) {
      setValue(initialValue);
    }
  }, [initialValue]);

  const handleChange = (valueString: string) => {
    setValue(valueString);
    setError('');
    if (controlled && onValueChange) {
      onValueChange(valueString);
    }
  };

  const handleSubmit = () => {
    const numValue = parseFloat(value);

    // Validate
    if (validation.required && !value) {
      setError('This field is required');
      return;
    }

    if (isNaN(numValue)) {
      setError('Please enter a valid number');
      return;
    }

    if (validation.min !== undefined && numValue < validation.min) {
      setError(`Minimum value is ${validation.min}`);
      return;
    }

    if (validation.max !== undefined && numValue > validation.max) {
      setError(`Maximum value is ${validation.max}`);
      return;
    }

    setError('');
    onSubmit(value);
  };

  return (
    <VStack spacing={4} align="stretch">
      {!controlled && (
        <Text fontSize="lg" fontWeight="medium" color="text-primary">
          {description}
        </Text>
      )}

      <NumberInput
        value={value}
        onChange={handleChange}
        min={validation.min}
        max={validation.max}
      >
        <NumberInputField
          placeholder="Enter a number..."
          bg="bg-surface"
          borderColor="border-default"
          _hover={{ borderColor: 'border-hover' }}
          _focus={{
            borderColor: 'brand.500',
            boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
          }}
        />
      </NumberInput>

      {validation.min !== undefined || validation.max !== undefined ? (
        <Text fontSize="xs" color="text-secondary">
          {validation.min !== undefined && `Min: ${validation.min}`}
          {validation.min !== undefined && validation.max !== undefined && ' | '}
          {validation.max !== undefined && `Max: ${validation.max}`}
        </Text>
      ) : null}

      {error && (
        <Text fontSize="sm" color="red.500">
          {error}
        </Text>
      )}

      {!controlled && (
        <Button
          colorScheme="brand"
          onClick={handleSubmit}
          isLoading={isLoading}
          isDisabled={validation.required && !value}
        >
          Submit
        </Button>
      )}
    </VStack>
  );
}